import React, { useState } from 'react';
import { Camera, Send, Trash2, Edit2, Image, Film, LayoutGrid } from 'lucide-react';
import { POST_TYPES, columnColors } from './constants';

// Kanban Column
const KanbanColumn = ({ title, subtitle, color, darkMode, posts, onEdit, onDelete, onPublish, onView, getInstanceName }) => {
  const cc = columnColors[color];
  return (
    <div className={`flex flex-col rounded-xl border-t-4 ${cc.header} ${darkMode ? 'bg-zinc-800/50 border-x border-b border-gray-700' : 'bg-gray-50 border-x border-b border-gray-200'}`}>
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${darkMode ? cc.countDark : cc.count}`}>{posts.length}</span>
        </div>
        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{subtitle}</p>
      </div>
      <div className="flex-1 px-3 pb-3 space-y-3 overflow-y-auto max-h-[75vh]">
        {posts.length === 0 ? (
          <div className={`text-center py-8 text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Nenhum post</div>
        ) : posts.map(post => (
          <PostCard key={post.id} post={post} darkMode={darkMode} onEdit={onEdit} onDelete={onDelete} onPublish={onPublish} onView={onView} getInstanceName={getInstanceName} />
        ))}
      </div>
    </div>
  );
};

// Post Card
const PostCard = ({ post, darkMode, onEdit, onDelete, onPublish, onView, getInstanceName }) => {
  const typeInfo = POST_TYPES.find(t => t.value === post.tipo) || POST_TYPES[0];
  const TypeIcon = typeInfo.icon;
  const isEditable = ['draft', 'scheduled', 'failed'].includes(post.status);
  const instanceName = getInstanceName(post.instancia_id);

  const statusBadge = {
    draft: { label: 'Rascunho', cls: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-300' },
    scheduled: { label: 'Agendado', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' },
    publishing: { label: 'Publicando...', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400' },
    published: { label: 'Publicado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' },
    failed: { label: 'Falhou', cls: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' },
  }[post.status] || { label: post.status, cls: 'bg-gray-100 text-gray-700' };

  return (
    <div onClick={() => onView(post)} className={`p-4 rounded-lg border transition-all hover:shadow-md cursor-pointer ${darkMode ? 'bg-zinc-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
      <div className="flex gap-3">
        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-pink-500/10 to-purple-600/10 flex items-center justify-center">
          {post.cover_url ? (
            <img src={post.cover_url} alt="" className="w-full h-full object-cover" />
          ) : post.media_urls?.[0] && !/\.(mp4|mov)$/i.test(post.media_urls[0]) ? (
            <img src={`${process.env.REACT_APP_API_URL}/instagram-posts/media/${encodeURIComponent(post.media_urls[0])}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <TypeIcon className={`w-6 h-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {post.caption || <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Sem legenda</span>}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-xs px-1.5 py-0.5 rounded ${statusBadge.cls}`}>{statusBadge.label}</span>
            <span className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <TypeIcon className="w-3 h-3" /> {typeInfo.label}
            </span>
            {instanceName && <span className={`text-xs ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}>{instanceName}</span>}
            {post.comment_trigger_active && <span className="text-xs" title={`Trigger: "${post.comment_trigger_keyword}"`}>💬⚡</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-zinc-700">
        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {post.scheduled_at && post.status === 'scheduled'
            ? `📅 ${new Date(post.scheduled_at + (post.scheduled_at.endsWith('Z') ? '' : 'Z')).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
            : post.published_at
            ? `✅ ${new Date(post.published_at + (post.published_at.endsWith?.('Z') ? '' : 'Z')).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
            : new Date(post.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </span>
        {isEditable && (
          <div className="flex gap-1">
            <button onClick={(e) => { e.stopPropagation(); onEdit(post); }} className={`p-1.5 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`} title="Editar">
              <Edit2 className="w-3.5 h-3.5 text-gray-400" />
            </button>
            {post.instancia_id && (
              <button onClick={(e) => { e.stopPropagation(); onPublish(post.id); }} className={`p-1.5 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`} title="Publicar agora">
                <Send className="w-3.5 h-3.5 text-pink-500" />
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); onDelete(post.id); }} className={`p-1.5 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`} title="Excluir">
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </button>
          </div>
        )}
      </div>
      {post.error_message && <p className="text-xs text-red-500 mt-1 truncate" title={post.error_message}>⚠️ {post.error_message}</p>}
    </div>
  );
};

const KanbanView = ({ darkMode, loading, drafts, scheduled, published, onEdit, onDelete, onPublish, onView, getInstanceName }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[1,2,3].map(i => <div key={i} className={`rounded-xl h-64 animate-pulse ${darkMode ? 'bg-zinc-800' : 'bg-gray-200'}`} />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
      <KanbanColumn title="📝 Rascunho" subtitle="Posts em preparação" color="yellow" darkMode={darkMode}
        posts={drafts} onEdit={onEdit} onDelete={onDelete} onPublish={onPublish} onView={onView} getInstanceName={getInstanceName} />
      <KanbanColumn title="📅 Agendado" subtitle="Aguardando publicação" color="blue" darkMode={darkMode}
        posts={scheduled} onEdit={onEdit} onDelete={onDelete} onPublish={onPublish} onView={onView} getInstanceName={getInstanceName} />
      <KanbanColumn title="✅ Publicado" subtitle="Posts publicados" color="green" darkMode={darkMode}
        posts={published} onEdit={onEdit} onDelete={onDelete} onPublish={onPublish} onView={onView} getInstanceName={getInstanceName} />
    </div>
  );
};

export default KanbanView;
